import { SPARKY_SERVER_URL, BLOCKED_PATTERNS, REDIRECT_RESPONSES } from '../shared/constants';
import { authHeaders } from '../../lib/apiAuth';

export function isSafeInput(text) {
  if (!text || typeof text !== "string") return true;
  const lower = text.toLowerCase().trim();
  for (const pattern of BLOCKED_PATTERNS) {
    if (pattern.test(lower)) return false;
  }
  return true;
}

export function getSafeRedirect() {
  return REDIRECT_RESPONSES[Math.floor(Math.random() * REDIRECT_RESPONSES.length)];
}

export function cleanSpeech(text) {
  return text
    .replace(/[\u{1F300}-\u{1FFFF}]/gu, "")
    .replace(/[\u{2600}-\u{26FF}]/gu, "")
    .replace(/[\u{2700}-\u{27BF}]/gu, "")
    .replace(/[\u{FE00}-\u{FE0F}]/gu, "")
    .replace(/[\u{1F000}-\u{1F02F}]/gu, "")
    .replace(/\b([A-Z]{2,})\b/g, w => w.charAt(0) + w.slice(1).toLowerCase())
    .replace(/[!]{2,}/g, "!")
    .replace(/[?]{2,}/g, "?")
    .replace(/\s+/g, " ")
    .trim();
}

export function fallbackSpeak(text, onStart, onEnd) {
  if (!("speechSynthesis" in window)) { if(onEnd) onEnd(); return; }
  window.speechSynthesis.cancel();
  const utt = new SpeechSynthesisUtterance(text);
  utt.rate  = 0.88;
  utt.pitch = 1.1;
  utt.volume = 1;
  const voices = window.speechSynthesis.getVoices();
  const pick = voices.find(v =>
    v.name.includes("Karen")   ||
    v.name.includes("Samantha") ||
    v.name.includes("Moira")   ||
    v.name.includes("Google UK English Female") ||
    v.name.includes("Microsoft Zira") ||
    (v.lang === "en-AU" && v.localService)
  ) || voices.find(v => v.lang?.startsWith("en") && v.localService);
  if (pick) utt.voice = pick;
  if (onStart) utt.onstart = onStart;
  utt.onend  = onEnd;
  utt.onerror = onEnd;
  window.speechSynthesis.speak(utt);
}

// ── Web Speech TTS ────────────────────────────────────────────
// ── Lumi Voice — OpenAI TTS with browser fallback ─────────────
let lumiAudio = null;

export async function speakText(text, onStart, onEnd) {
  if (!text) { if(onEnd) onEnd(); return; }
  const clean = cleanSpeech(text);
  if (!clean) { if(onEnd) onEnd(); return; }

  // Stop any current speech
  window.speechSynthesis?.cancel();
  if (lumiAudio) { lumiAudio.pause(); lumiAudio = null; }

  if (onStart) onStart();

  // ── Web Speech API first — zero network delay ──────────────
  if (window.speechSynthesis) {
    const utt = new SpeechSynthesisUtterance(clean);
    utt.rate = 0.92;
    utt.pitch = 1.1;
    utt.volume = 1;
    const voices = window.speechSynthesis.getVoices();
    const preferred = voices.find(v =>
      v.name.includes('Samantha') || v.name.includes('Karen') ||
      v.name.includes('Moira')    || v.name.includes('Tessa') ||
      v.lang === 'en-AU' || v.lang === 'en-NZ'
    ) || voices.find(v => v.lang.startsWith('en-') && !v.name.includes('Google'))
      || voices.find(v => v.lang.startsWith('en'))
      || voices[0];
    if (preferred) utt.voice = preferred;
    utt.onend   = () => { if (onEnd) onEnd(); };
    utt.onerror = () => { if (onEnd) onEnd(); };
    window.speechSynthesis.speak(utt);
    return;
  }

  // ── Fallback: Railway OpenAI TTS (only if Web Speech unavailable) ──
  try {
    const res = await fetch(SPARKY_SERVER_URL + '/api/tts', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
      body: JSON.stringify({ text: clean }),
    });
    if (res.ok) {
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      lumiAudio  = new Audio(url);
      lumiAudio.onended = () => { URL.revokeObjectURL(url); if (onEnd) onEnd(); };
      lumiAudio.onerror = () => { fallbackSpeak(clean, null, onEnd); };
      lumiAudio.play();
      return;
    }
  } catch(e) {}

  fallbackSpeak(clean, null, onEnd);
}

export function detectTopic(text) {
  const t = text.toLowerCase();
  if (t.match(/pythagoras|right.?angle|hypotenuse|a.squared|b.squared/)) return "Pythagoras theorem";
  if (t.match(/fraction|numerator|denominator|half|quarter|thirds|eighths/)) return "fractions";
  if (t.match(/angle|acute|obtuse|reflex|protractor|degree/)) return "angles";
  if (t.match(/circle|radius|diameter|circumference|pi/)) return "circles";
  if (t.match(/area|perimeter|rectangle|square|length.*width/)) return "area and perimeter";
  if (t.match(/food.?web|food.?chain|predator|prey|producer|consumer/)) return "food webs";
  if (t.match(/number.?line|sequence|pattern|next.?number/)) return "number patterns";
  if (t.match(/place.?value|hundreds|tens|ones|digit/)) return "place value";
  if (t.match(/infer|inference|clue|detective|read.?between/)) return "inference";
  if (t.match(/multipl|times.?table|groups.?of/)) return "multiplication";
  if (t.match(/divis|divide|quotient|remainder/)) return "division";
  if (t.match(/percent|proportion|ratio/)) return "percentages and ratios";
  if (t.match(/algebra|variable|equation|solve.?for/)) return "algebra";
  if (t.match(/decimal|point|tenths|hundredths/)) return "decimals";
  if (t.match(/probability|chance|likely|unlikely/)) return "probability";
  if (t.match(/noun|verb|adjective|adverb|pronoun|conjunction/)) return "grammar";
  if (t.match(/simile|metaphor|personif|alliter|onomatop/)) return "literary devices";
  if (t.match(/persuasive|argument|opinion|evidence/)) return "persuasive writing";
  if (t.match(/narrative|story|plot|character/)) return "narrative writing";
  if (t.match(/photosynthesis|plant|chlorophyll/)) return "photosynthesis";
  if (t.match(/cell|nucleus|membrane|mitochondria/)) return "cells";
  if (t.match(/force|gravity|friction|newton/)) return "forces";
  if (t.match(/electricity|circuit|current|voltage/)) return "electricity";

  return null;
}

export function detectDiagram(text) {
  const t = text.toLowerCase();
  if (t.includes('pythagoras') || t.includes('right-angled triangle') || t.includes('right angle triangle') || (t.includes('a\u00B2') && t.includes('b\u00B2'))) return 'pythagoras';
  if (t.includes('fraction') && (t.includes('numerator') || t.includes('denominator') || t.includes('half') || t.includes('quarter'))) return 'fractions';
  if (t.includes('number line') || t.includes('number pattern') || t.includes('sequence')) return 'numberline';
  if (t.includes('angle') && (t.includes('degree') || t.includes('\u00B0') || t.includes('acute') || t.includes('obtuse'))) return 'angles';
  if (t.includes('area') && t.includes('rectangle')) return 'rectangle';
  if (t.includes('circle') && (t.includes('radius') || t.includes('diameter') || t.includes('circumference'))) return 'circle';
  if (t.includes('food web') || t.includes('food chain') || t.includes('predator') || t.includes('prey')) return 'foodweb';
  if (t.includes('fraction bar') || t.includes('equivalent fraction')) return 'fractions';

  return null;
}
